import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field, Query } from '@adornis/baseql/decorators.js';
import { type PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime, Interval } from 'luxon';
import { ChancenEventlog } from './ChancenEventlog.js';

@Entity()
export class SessionEventLog extends ChancenEventlog {
  static override _class = 'SessionEventLog';

  @validate(nonOptional())
  @Field(type => String)
  sessionEventType!: 'start' | 'end';

  @validate(nonOptional())
  @Field(type => String)
  app!: string;

  constructor(doc: PartialEntityData<SessionEventLog>) {
    super(doc);
  }

  @Query(type => Float)
  static getAverageSessionLength() {
    return async () => {
      const col = await getRawCollection(this._collectionName);
      const sessions = await col
        .aggregate([
          {
            $match: {
              createdAt: { $gte: DateTime.now().minus({ month: 1 }).toJSDate() },
              _class: this._class,
              sessionEventType: 'start',
            },
          },
          {
            $lookup: {
              from: this._collectionName,
              as: 'sessionEnd',
              let: { sessionID: '$clientSession', startDate: '$createdAt' },
              pipeline: [
                {
                  $match: {
                    $expr: {
                      $and: [
                        { $eq: ['$_class', this._class] },
                        { $eq: ['$sessionEventType', 'end'] },
                        { $eq: ['$clientSession', '$$sessionID'] },
                        { $gte: ['$createdAt', '$$startDate'] },
                      ],
                    },
                  },
                },
                { $sort: { createdAt: 1 } },
                { $limit: 1 },
              ],
            },
          },
          { $unwind: '$sessionEnd' },
        ])
        .toArray();

      const sessionLengths = sessions.map(session => {
        const start = DateTime.fromJSDate(session.createdAt);
        const end = DateTime.fromJSDate(session.sessionEnd.createdAt);
        return (start.until(end) as Interval).toDuration().as('minutes');
      });

      return sessionLengths.reduce((acc, curr) => acc + curr, 0) / sessionLengths.length;
    };
  }

  @Query(type => Float)
  static getActiveUsersLastMonth() {
    return async () => {
      const col = await getRawCollection(this._collectionName);
      const sessions = await col
        .aggregate([
          {
            $match: {
              createdAt: { $gte: DateTime.now().minus({ month: 1 }).toJSDate() },
              _class: this._class,
              sessionEventType: 'start',
            },
          },
          { $group: { _id: '$userID' } },
        ])
        .toArray();

      const userIDs = sessions.map(session => session._id).filter(Boolean);
      return userIDs.length;
    };
  }
}
